/**
 * Safe wrappers for sessionStorage and localStorage.
 *
 * Browser storage APIs can throw SecurityError in restricted contexts
 * (private browsing, sandboxed iframes, cookies disabled). These wrappers
 * follow the same pattern used by usehooks-ts and react-use: try-catch
 * around every access, returning fallback values on failure.
 */

function createSafeStorage(type: 'session' | 'local') {
  function getStorage(): Storage | null {
    try {
      return type === 'session' ? window.sessionStorage : window.localStorage;
    } catch {
      return null;
    }
  }

  return {
    getItem(key: string): string | null {
      try {
        return getStorage()?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): void {
      try {
        getStorage()?.setItem(key, value);
      } catch { /* storage unavailable */ }
    },
    removeItem(key: string): void {
      try {
        getStorage()?.removeItem(key);
      } catch { /* storage unavailable */ }
    },
  };
}

export const safeSessionStorage = createSafeStorage('session');
export const safeLocalStorage = createSafeStorage('local');
